/**
 * PricingCard Component - v2
 * 
 * Individual pricing tier card with price, features, and CTA.
 * Supports highlight state for recommended plan.
 * 
 * v2 Changes:
 * - Removed yearly tier support
 * - Added trial badge and "NO PAYMENT TODAY" for monthly
 * - Updated lifetime to $49
 * - Added trust signals
 */

import { PRICING, PricingPlan, TRUST_SIGNALS } from './constants';
import { useLifetimeStatus } from '@/hooks/useLifetimeStatus';
import { useLanguage } from '@/i18n';
import './PricingCard.css';

interface PricingCardProps {
  plan: PricingPlan;
  highlighted?: boolean;
  onSelect: (plan: PricingPlan) => void;
  variant?: 'default' | 'compact' | 'horizontal';
}

export function PricingCard({ 
  plan, 
  highlighted = false, 
  onSelect,
  variant = 'default',
}: PricingCardProps) {
  const { t } = useLanguage();
  const pricing = PRICING[plan];
  const isLifetime = plan === 'lifetime';
  const isMonthly = plan === 'monthly';
  
  // Fetch real lifetime status from API
  const lifetimeStatus = useLifetimeStatus();

  // Type-safe access for optional properties
  const badge = pricing.badge;
  const remaining = isLifetime ? lifetimeStatus.remaining : null;
  const total = isLifetime ? lifetimeStatus.total : null;
  const trialDays = isMonthly ? PRICING.monthly.trialDays : null;
  const noPaymentToday = isMonthly ? PRICING.monthly.noPaymentToday : false;

  const handleClick = () => {
    onSelect(plan);
  };

  return (
    <div 
      className={`
        pricing-card 
        pricing-card--${variant}
        ${highlighted ? 'pricing-card--highlighted' : ''}
        ${isLifetime ? 'pricing-card--lifetime' : ''}
        ${isMonthly ? 'pricing-card--monthly' : ''}
      `}
    >
      {/* Badge */}
      {badge && (
        <div className={`pricing-badge ${isMonthly ? 'pricing-badge--trial' : 'pricing-badge--value'}`}>
          {t(badge)}
        </div>
      )}
      
      {/* Scarcity for lifetime */}
      {isLifetime && remaining !== null && total !== null && (
        <div className="pricing-scarcity">
          {t('funnel.scarcity', { remaining, total })}
        </div>
      )}

      {/* Plan name */}
      <div className="pricing-plan-name">
        {isMonthly && t('funnel.plan_monthly')}
        {isLifetime && t('funnel.plan_lifetime')}
      </div>

      {/* Trial highlight for monthly */}
      {isMonthly && trialDays && (
        <div className="pricing-trial-badge">
          {t('funnel.trial_badge', { days: trialDays })}
        </div>
      )}

      {/* Price */}
      <div className="pricing-price-container">
        <span className="pricing-currency">$</span>
        <span className="pricing-amount">
          {isMonthly ? '4.99' : '49'}
        </span>
        {isMonthly && (
          <span className="pricing-period">{t('funnel.per_month')}</span>
        )}
      </div>

      {/* No payment today for trial */}
      {isMonthly && noPaymentToday && (
        <div className="pricing-no-payment">
          {t(TRUST_SIGNALS.noPaymentToday)}
        </div>
      )}

      {/* Lifetime one-time note */}
      {isLifetime && (
        <div className="pricing-one-time">{t('funnel.one_time')}</div>
      )}

      {/* CTA Button */}
      <button 
        className={`pricing-cta ${highlighted ? 'pricing-cta--primary' : ''} ${isMonthly ? 'pricing-cta--trial' : ''}`}
        onClick={handleClick}
      >
        {t(pricing.ctaText)}
      </button>

      {/* Cancel anytime for monthly */}
      {isMonthly && (
        <div className="pricing-cancel-note">
          {t(TRUST_SIGNALS.cancelAnytime)}
        </div>
      )}
    </div>
  );
}

// Horizontal button variant for modal - v2
interface PricingButtonProps {
  plan: PricingPlan;
  highlighted?: boolean;
  onSelect: (plan: PricingPlan) => void;
}

export function PricingButton({ plan, highlighted = false, onSelect }: PricingButtonProps) {
  const { t } = useLanguage();
  const pricing = PRICING[plan];
  const isLifetime = plan === 'lifetime';
  const isMonthly = plan === 'monthly';
  
  // Fetch real lifetime status from API
  const lifetimeStatus = useLifetimeStatus();

  // Type-safe access for optional properties
  const remaining = isLifetime ? lifetimeStatus.remaining : null;
  const trialDays = isMonthly ? PRICING.monthly.trialDays : null;

  return (
    <button 
      className={`
        pricing-button 
        ${highlighted ? 'pricing-button--highlighted' : ''}
        ${isLifetime ? 'pricing-button--lifetime' : ''}
        ${isMonthly ? 'pricing-button--monthly' : ''}
      `}
      onClick={() => onSelect(plan)}
    >
      <div className="pricing-button-content">
        <span className="pricing-button-label">
          {isLifetime ? t('funnel.plan_lifetime') : isMonthly ? t('funnel.plan_monthly') : pricing.label}
        </span>
        {isMonthly && trialDays && (
          <span className="pricing-button-trial">{t('funnel.trial_short', { days: trialDays })}</span>
        )}
        {isLifetime && (
          <span className="pricing-button-price">{pricing.label}</span>
        )}
        {isMonthly && (
          <span className="pricing-button-price">{t('funnel.then_price', { price: t(pricing.label) })}</span>
        )}
        {isLifetime && remaining !== null && (
          <span className="pricing-button-scarcity">{t('funnel.left_short', { count: remaining })}</span>
        )}
      </div>
      {pricing.badge && <span className="pricing-button-badge">{t(pricing.badge)}</span>}
    </button>
  );
}
